import { css } from "@emotion/react";
import { darken } from "@mui/material/styles";
import colorPalette from "@unit/styles/src/app-style/color-palette";

import { appColor } from "./app-colors";
import { FontColor, FontFamily, FontWeight } from "./app-style/font-style";
import { appTypo } from "./app-typography";

export const CustomMuiButton = css`
  font-family: ${appTypo.font.notoJp};
  color: ${appColor.App.white};
  text-transform: none;
  background: ${appColor.Button.primary.color};
  opacity: 0.95;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.05em;
  padding: 10px 20px;
  &:hover {
    background: ${appColor.Button.primary.color};
    opacity: 1;
  }
  &.Mui-disabled {
    opacity: 0.4;
    color: ${appColor.App.white};
    background: ${appColor.Button.primary.color};
  }
  & a:focus {
    color: ${appColor.App.white};
  }
`;

export const CustomMuiButtonSmall = css`
  cursor: pointer;
  font-family: ${appTypo.font.notoJp};
  color: ${appColor.App.white};
  background: ${appColor.Button.primary.color};
  opacity: 0.95;
  &:hover {
    color: ${appColor.App.white};
    background: ${appColor.Button.primary.color};
    opacity: 1;
  }
  &.Mui-disabled {
    opacity: 0.4;
    color: ${appColor.App.white};
    background: ${appColor.Button.primary.color};
  }
  & a:focus {
    color: ${appColor.App.white};
  }
`;

export const CustomMuiButtonRed = css`
  ${CustomMuiButton};
  background: ${appColor.App.accent};
  &:hover {
    background: ${appColor.App.accent};
  }
  &.Mui-disabled {
    background: ${appColor.App.accent};
  }
`;

export const CustomMuiButtonRedSmall = css`
  ${CustomMuiButtonSmall};
  background: ${appColor.App.accent};
  &:hover {
    background: ${appColor.App.accent};
  }
  &.Mui-disabled {
    background: ${appColor.App.accent};
  }
`;

export const CustomMuiButtonCustom = (colorCode: string) => css`
  ${CustomMuiButtonSmall};
  background: ${colorCode};
  &:hover {
    background: ${colorCode};
  }
  &.Mui-disabled {
    background: ${colorCode};
  }
`;
export const CustomMuiButtonCustomSmall = (colorCode: string) => css`
  ${CustomMuiButtonSmall};
  background: ${colorCode};
  &:hover {
    background: ${colorCode};
    color: ${appColor.App.white};
    opacity: 0.8;
  }
  &.Mui-disabled {
    background: ${colorCode};
  }
`;

export const CustomMuiButtonBorderSmall = (backgroundColorCode: string, borderColorCode: string, color: string) => css`
  ${CustomMuiButtonCustomSmall(backgroundColorCode)};
  border: 1px solid ${borderColorCode};
  color: ${color};

  &:hover {
    color: ${color};
  }
`;

export const CustomAppWhiteButton = css`
  background: ${appColor.App.white};
  font-family: ${appTypo.font.notoJp};
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  color: ${appColor.Text.primary};
  border: 1px solid ${appColor.Text.primary};
  box-sizing: border-box;
  border-radius: 16px;
  &.MuiButtonBase-root {
    box-shadow: none !important;
  }
  &:hover {
    background: ${appColor.App.white};
    opacity: 1;
  }
  &.Mui-disabled {
    opacity: 0.8;
    background: ${appColor.App.white};
  }
  & a:focus {
    color: ${appColor.App.white};
  }
`;

export const FormStyled = css`
  span {
    font-family: ${appTypo.font.notoJp} !important;
  }
  width: 100%;
  border-radius: 8px;
  background: ${appColor.Border.secondary};
  & .MuiOutlinedInput-root {
    &:hover {
      border-color: ${appColor.App.primary};
    }
    border-radius: 8px;
    fieldset {
      border-color: ${appColor.Border.secondary};
    }
  }
`;

export const CustomMuiSelect = css`
  font-family: ${appTypo.font.notoJp} !important;
  border-radius: 8px;
  & .MuiOutlinedInput-input {
    color: ${appColor.Text.primary};
    background: ${appColor.Border.secondary};
  }
  fieldset {
    border: none;
  }
`;

export const CustomMuiDrawerBody = css`
  max-height: calc(100vh - 172px);
  overflow-y: auto;
`;

export const CustomMuiButtonGradientBlue = css`
  ${CustomMuiButton};
  background: ${appColor.App.welcome};
  &:hover {
    background: ${appColor.App.welcome};
  }
  &.Mui-disabled {
    background: ${appColor.App.welcome};
  }
`;

export const FormStyledMultiple = css`
  span {
    font-family: ${appTypo.font.kintoSans} !important;
  }
  width: 100%;
  border-radius: 4px;
  textarea {
    background: ${appColor.Border.secondary};
  }
  & .MuiInputBase-multiline {
    background: ${appColor.Border.secondary};
    &:hover {
      border-color: ${appColor.App.primary};
    }
    border-radius: 4px;
    fieldset {
      border-color: ${appColor.Border.secondary};
    }
  }
`;

export const AppCursorPointer = css`
  cursor: pointer;
`;

export const CustomMuiButtonBlue = css`
  font-family: ${FontFamily.kintoSans};
  font-weight: ${FontWeight.medium};
  color: ${FontColor.white};
  font-size: 14px;
  background: ${colorPalette.cerulean};
  border-radius: 32px;
  &:hover {
    background: ${colorPalette.cerulean};
  }
  &.Mui-disabled {
    background: ${colorPalette.cerulean};
  }
`;

/**
 * 一覧の行にある操作アイコン（編集・詳細・PW）の見た目。
 *
 * デモ（運営ダッシュの参照実装）は枠線のみの淡い丸で、行の内容より目立たせない。
 * 以前は申込・顧客管理簿が各自で同じ定義を持ち、代理店・代理店スタッフだけ
 * 塗りつぶしの `CustomMuiButtonSmall` を使っていて画面ごとに印象が違った（#1723）。
 *
 * 色は appColor のトークンへ寄せていない。`App.accent` は赤で意味が違い、
 * ここで欲しいのはデモと同じ淡いグレー枠＋ホバーで青、という具体値のため。
 */
export const CustomMuiRowActionButton = css`
  width: 28px;
  height: 28px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  color: #9ca3af;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background-color: #ffffff;
    border-color: rgba(58, 183, 255, 0.4);
    color: #3ab7ff;
  }
`;

/**
 * 一覧ヘッダの主要アクション（新規登録・申込フォームを開く）の見た目。
 *
 * デモ（運営ダッシュの参照実装）は 4 画面とも青塗り（ブランド色 #3AB7FF）で、
 * 「このページで新しく作る」操作を1色に揃えている。既存の admin-dash は黒塗り
 * （`Button.primary`）だが、運営ダッシュの新機能（申込・顧客管理簿・代理店・
 * 代理店スタッフ）はデモを要件の正として扱ってきたので、この 4 画面だけ青へ寄せる（#1723）。
 *
 * 色はブランド色のトークン（`colorPalette.dodgerBlue` ＝ デモの #3AB7FF と同値）を使い、
 * ホバーはそこから `darken` で導く。
 */
export const CustomMuiButtonActionBlue = css`
  ${CustomMuiButtonCustomSmall(colorPalette.dodgerBlue)};
  &:hover {
    /* ブランド色を1段暗くした派生色。値を直書きすると、ブランド色を変えたときに
       ホバーだけ取り残される。 */
    background: ${darken(colorPalette.dodgerBlue, 0.1)};
    opacity: 1;
  }
`;

/**
 * 一覧の絞り込み欄（検索・セレクト・Autocomplete）の高さを詰める。
 *
 * MUI の `size="small"` は約40px だが、デモ（運営ダッシュの参照実装）は約32px で、
 * 絞り込みが1行に収まり一覧の可視行数が増える（#1727）。入力欄そのものの高さを
 * 詰めるだけで、ラベルや配置は触らない。
 */
export const CustomMuiCompactFilter = css`
  & .MuiOutlinedInput-root {
    font-size: 13px;
  }
  & .MuiOutlinedInput-input {
    padding-top: 6px;
    padding-bottom: 6px;
  }
  & .MuiAutocomplete-inputRoot {
    padding-top: 1px;
    padding-bottom: 1px;
  }
  & .MuiSelect-select {
    padding-top: 6px;
    padding-bottom: 6px;
    min-height: 0;
  }
  & .MuiInputLabel-root {
    font-size: 13px;
  }
`;
